import * as React from 'react';
import {Mutation} from 'react-apollo';

import {api} from 'nutshell-core/api';
import {copyToClipboard} from 'nutshell-core/utils/copy-to-clipboard';

import {ActionMenu} from 'shells/action-menu/action-menu';
import {ConfirmationDialog} from 'shells/confirmation-dialog/confirmation-dialog';
import {
    PandadocIcon,
    CloudDownloadIcon,
    TrashIcon,
    TrashEmptyIcon,
    CloneIcon,
    EmailIcon,
    LayoutRegularIcon,
} from 'shells/icon';
import {Link} from 'shells/link/link';
import {Body} from 'shells/typography/body';

import type {
    Lead as LeadFragment,
    AddEntityToConstantContactList as AddEntityToConstantContactListMutation,
    AddEntityToConstantContactListVariables as AddEntityToConstantContactListMutationVariables,
    QuickbooksCreateInvoiceForLead,
    QuickbooksCreateInvoiceForLeadVariables,
} from 'nutshell-graphql-types';

import {ConstantContactPopover} from '../../../core/constant-contact/constant-contact-popover/constant-contact-popover';
import {QuickbooksLeadPopover} from '../../../core/quickbooks/quickbooks-lead-popover';
import {useGetSessionUser} from '../../../core/use-get-session-user';
import {getLayoutCustomizerPath} from '../../../core-entity/layout-customizer/helpers';
import {HelpClosedLeadFollowupAutomations} from '../../../sales-automation/onboarding/help-closed-lead-followup-automations/help-closed-lead-followup-automations';

import {appHistory} from '../../../../history';
import {
    shouldShowMailchimp,
    shouldShowConstantContact,
} from '../../../../integrations/should-show-buttons';
import {getDefaultStagesetId} from '../../../../util/get-default-stageset-id';
import {useDismissibleUserSetting} from '../../../../util/hooks/use-dismissible-user-setting';

import ADD_ENTITY_TO_CTCT_LIST from '../../shared/graphql/mutations/add-entity-to-ctct.graphql';
import CREATE_INVOICE_IN_QUICKBOOKS from '../../shared/graphql/mutations/create-invoice-for-lead-in-quickbooks.graphql';

import styles from './lead-buttons.module.css';

const FOLLOWUP_ONBOARDING_SETTINGS_KEY = 'has_dismissed_lead_followup_onboarding';

type Props = {
    lead: LeadFragment;
    // Used on the new lead header to make the action menu smaller
    isSmall?: boolean;
};

class AddEntityToCtCtMutation extends Mutation<
    AddEntityToConstantContactListMutation,
    AddEntityToConstantContactListMutationVariables
> {}

class QuickbooksCreateInfoiceForLeadMutation extends Mutation<
    QuickbooksCreateInvoiceForLead,
    QuickbooksCreateInvoiceForLeadVariables
> {}

export function LeadActionMenu(props: Props) {
    const {isSmall = false} = props;

    const [isAttemptingToDeleteLead, setIsAttemptingToDeleteLead] = React.useState(false);
    const [isAttemptingToHardDeleteLead, setIsAttemptingToHardDeleteLead] = React.useState(false);
    const [isShowingCtCtPopover, setIsShowingCtCtPopover] = React.useState(false);
    const [isShowingQuickbooksPopover, setIsShowingQuickbooksPopover] = React.useState(false);
    const [isOnboardingOpen, setIsOnboardingOpen] = React.useState(false);

    const {user} = useGetSessionUser();
    const {shouldShow, onDismiss} = useDismissibleUserSetting(FOLLOWUP_ONBOARDING_SETTINGS_KEY);

    // This div is needed to show the mailchimp popover.
    // Without it, any div that calls the popover unmounts
    // when the menu goes away, so we need an always rendered
    // div to mount.
    const mailchimpPopoverDivRef = React.useRef<HTMLElement | null | undefined>(null);

    const leadNameElement = document.getElementById('lead-name');

    const onDeleteCancel = () => {
        setIsAttemptingToDeleteLead(false);
        setIsAttemptingToHardDeleteLead(false);
    };

    // add csrf tokens to these confirms
    const onDeleteConfirm = () => {
        setIsAttemptingToDeleteLead(false);

        Nut.ajax.post({
            data: {csrfToken: Nut.getCsrfTokenFromDom()},
            // reload result
            url: `/lead/delete-lead/${props.lead.id}.json`,
        });
    };

    const onHardDeleteConfirm = () => {
        setIsAttemptingToHardDeleteLead(false);

        Nut.ajax.post({
            data: {csrfToken: Nut.getCsrfTokenFromDom()},
            url: `/lead/${props.lead.id}/hard-delete.json`,
        });
    };

    const handleSendToMailchimp = () => {
        if (mailchimpPopoverDivRef.current) {
            mailchimpPopoverDivRef.current.click();
        }
    };

    const handleDeleteSelect = () => {
        if (props.lead.deletedTime === null) {
            setIsAttemptingToDeleteLead(true);
        } else {
            setIsAttemptingToHardDeleteLead(true);
        }
    };

    const handleShowCtCtPopover = () => {
        setIsShowingCtCtPopover(true);
    };

    const handleHideCtCtPopover = () => {
        setIsShowingCtCtPopover(false);
    };

    const handleShowQuickbooksPopover = () => {
        setIsShowingQuickbooksPopover(true);
    };

    const handleHideQuickbooksPopover = () => {
        setIsShowingQuickbooksPopover(false);
    };

    const handleCloneLead = () => {
        if (
            !NutClientConfig.hasFollowupLeadAutomationsFF ||
            !user ||
            !user.permissions.canAccessSetup ||
            !shouldShow
        ) {
            // using this instead of appHistory because we actually want a full page reload
            window.location.href = `/lead/clone-lead?id=${props.lead.id}`;
        } else {
            setIsOnboardingOpen(true);
        }
    };

    const handleCloseOnboarding = () => {
        onDismiss?.();
        setIsOnboardingOpen(false);
    };

    const handleFinishOnboarding = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        onDismiss &&
            onDismiss().then(() => {
                appHistory.push(`/sales/pipelines/${getDefaultStagesetId()}/closed-leads`);
            });
    };

    return (
        <AddEntityToCtCtMutation mutation={ADD_ENTITY_TO_CTCT_LIST}>
            {(addEntityToCtCt) => (
                <QuickbooksCreateInfoiceForLeadMutation mutation={CREATE_INVOICE_IN_QUICKBOOKS}>
                    {(quickbooksCreateInvoiceForLead) => (
                        <React.Fragment>
                            <div
                                ref={(node) => {
                                    mailchimpPopoverDivRef.current = node;
                                }}
                                className='popover-opener-mailchimp popover-opener-ajax'
                                style={{margin: 0}} // using style to override ButtonGroup's specificity
                                data-url='/send-to/lead-mailchimp-popover.json'
                                data-target-selector='#lead-name'
                            />
                            <ActionMenu
                                options={buildMenuOptions(
                                    props.lead,
                                    user,
                                    handleDeleteSelect,
                                    handleSendToMailchimp,
                                    handleShowCtCtPopover,
                                    handleShowQuickbooksPopover,
                                    handleCloneLead
                                )}
                                size={isSmall ? 'small' : undefined}
                                padding={isSmall ? 'less' : undefined}
                            />
                            {isAttemptingToDeleteLead ? (
                                // @ts-expect-error
                                <ConfirmationDialog
                                    title='Are you sure you want to delete this lead?'
                                    onCancel={onDeleteCancel}
                                    onConfirm={onDeleteConfirm}
                                />
                            ) : null}
                            {isAttemptingToHardDeleteLead ? (
                                // @ts-expect-error
                                <ConfirmationDialog
                                    title='Are you sure you want to permanently delete this lead? This cannot be undone.'
                                    onCancel={onDeleteCancel}
                                    onConfirm={onHardDeleteConfirm}
                                />
                            ) : null}
                            {isShowingCtCtPopover ? (
                                <ConstantContactPopover
                                    anchor={leadNameElement}
                                    location='bottom-right'
                                    submitButtonText='Send'
                                    canAccessIntegrations={Boolean(
                                        user && user.permissions.canAccessSetup
                                    )}
                                    onCancel={handleHideCtCtPopover}
                                    onSuccess={() => {
                                        handleHideCtCtPopover();
                                        Popmini.open('Successfully sent to Constant Contact.', {
                                            attachTo: leadNameElement,
                                            classes: 'type-message',
                                            orientation: 'east',
                                        });
                                    }}
                                    onSubmit={(data) => {
                                        if (!data.listId) {
                                            return Promise.reject(
                                                'Choose a constant contact list.'
                                            );
                                        }

                                        const leadId = props.lead.id;

                                        return addEntityToCtCt({
                                            variables: {
                                                input: {
                                                    entityId: leadId,
                                                    listId: data.listId,
                                                },
                                            },
                                            // TODO (@ianvs): an `update` would be nice here, but it's a bit complex,
                                            // and page changes to related peeps are full page loads, so we don't
                                            // have to worry _too_ much about the cache, I think.
                                        });
                                    }}
                                />
                            ) : null}
                            {isShowingQuickbooksPopover ? (
                                <QuickbooksLeadPopover
                                    anchor={leadNameElement}
                                    location='bottom-right'
                                    lead={props.lead}
                                    submitButtonText='Create Invoice'
                                    canAccessIntegrations={Boolean(
                                        user && user.permissions.canAccessSetup
                                    )}
                                    onCancel={handleHideQuickbooksPopover}
                                    onSuccess={() => {}}
                                    onSubmit={() => {
                                        // This should not happen, but makes flow happy.
                                        if (!props.lead) {
                                            return Promise.reject(
                                                new Error(
                                                    'We weren’t able to create an invoice for this lead in Quickbooks.  Please try again.'
                                                )
                                            );
                                        }

                                        const leadId = props.lead.id;

                                        return quickbooksCreateInvoiceForLead({
                                            variables: {
                                                input: {
                                                    leadId: leadId,
                                                },
                                            },
                                        });
                                    }}
                                />
                            ) : null}
                            <HelpClosedLeadFollowupAutomations
                                isOpen={isOnboardingOpen}
                                onClose={handleCloseOnboarding}
                                primaryAction={handleFinishOnboarding}
                                primaryButtonText='View follow-up automations'
                                secondaryAction={() => {
                                    handleCloseOnboarding();
                                    window.location.href = `/lead/clone-lead?id=${props.lead.id}`;
                                }}
                                secondaryButtonText='Continue cloning'
                            />
                        </React.Fragment>
                    )}
                </QuickbooksCreateInfoiceForLeadMutation>
            )}
        </AddEntityToCtCtMutation>
    );
}

function buildMenuOptions(
    // @ts-expect-error
    lead,
    // @ts-expect-error
    user,
    // @ts-expect-error
    onDeleteSelect,
    // @ts-expect-error
    onMailchimpSelect,
    // @ts-expect-error
    onCtCtSelect,
    // @ts-expect-error
    onQuickbooksSelect,
    // @ts-expect-error
    onCloneSelect
) {
    let optionKey = 1;

    const menuOptions = [
        {
            label: <Body>Clone this lead</Body>,
            handleSelect: onCloneSelect,
            key: `${optionKey++}`,
            sortKey: 'Clone this lead',
            icon: <CloneIcon size={20} />,
        },
    ];

    if (
        NutClientConfig.hasCoreEntityFullHeightSidebarFF &&
        user &&
        user.permissions.canAccessSetup
    ) {
        menuOptions.push({
            label: <Body>Customize layout</Body>,
            handleSelect: () => {
                appHistory.push(getLayoutCustomizerPath('LEAD', lead.stagesetForWidgets?.id));
            },
            key: `${optionKey++}`,
            sortKey: 'Customize layout',
            icon: <LayoutRegularIcon size={20} />,
        });
    }

    menuOptions.push({
        label: <div className={styles.button}>Copy BCC address</div>,
        handleSelect: () => {
            copyToClipboard(lead.bccAddress);
        },
        // @ts-expect-error
        tooltipTextOnClose: `Copied! ${lead.bccAddress}`,
        key: `${optionKey++}`,
        sortKey: 'Copy BCC address',
        icon: <EmailIcon size={20} />,
    });

    if (NutClientConfig.hasQuoteroller) {
        // @ts-expect-error
        menuOptions.push({
            label: (
                // @ts-expect-error
                <Link href={`/send-to/quoteroller?id=${lead.id}`} target='_blank'>
                    Send to QuoteRoller
                </Link>
            ),
            sortKey: 'Send to QuoteRoller',
            key: `${optionKey++}`,
            icon: (
                <img
                    src='/include/images/integration-logos/quote-roller.png'
                    className={styles.logo}
                />
            ),
        });
    }

    if (NutClientConfig.hasRightSignatureNUT6758) {
        // @ts-expect-error
        menuOptions.push({
            label: (
                // @ts-expect-error
                <Link href={`/send-to/rightsignature?id=${lead.id}`} target='_blank'>
                    Send to RightSignature
                </Link>
            ),
            sortKey: 'Send to RightSignature',
            key: `${optionKey++}`,
            icon: (
                <img
                    src='/include/images/integration-logos/right-signature.JPEG'
                    className={styles.logo}
                />
            ),
        });
    }

    menuOptions.push(
        // @ts-expect-error
        {
            label: (
                // @ts-expect-error
                <Link href={`/send-to/pandadoc?id=${lead.id}`} target='_blank'>
                    Send to PandaDoc
                </Link>
            ),
            sortKey: 'Send to PandaDoc',
            key: `${optionKey++}`,
            icon: <PandadocIcon size={24} />,
        },
        {
            label: <div className={styles.button}>Download as CSV</div>,
            variant: 'secondary',
            sortKey: 'Download as CSV',
            handleSelect: () => {
                const href = `/leads/list?selectedIds=${lead.id}&allData=true`;

                api.get(href, undefined, {
                    headers: {
                        Accept: 'text/csv; charset=utf-8',
                    },
                }).then(() => {
                    // This is a gearman job, and albeit a fast one,
                    // we don't get the download back right away, so we need
                    // to poll for it.
                    Nut.pollForNotifications();
                });
            },
            key: `${optionKey++}`,
            icon: <CloudDownloadIcon size={20} />,
        }
    );

    if (shouldShowMailchimp(NutClientConfig.integrations)) {
        menuOptions.push({
            label: <div className={styles.button}>Send to Mailchimp</div>,
            // @ts-expect-error
            variant: 'secondary',
            handleSelect: onMailchimpSelect,
            key: `${optionKey++}`,
            sortKey: 'Send to Mailchimp',
            icon: (
                <img
                    src='/include/images/integration-logos/mailchimp-freddy.png'
                    className={styles.logo}
                />
            ),
        });
    }

    if (shouldShowConstantContact(NutClientConfig.integrations)) {
        menuOptions.push({
            label: <div className={styles.button}>Send to Constant Contact</div>,
            key: `${optionKey++}`,
            sortKey: 'Send to Constant Contact',
            handleSelect: onCtCtSelect,
            icon: (
                <img
                    src='/include/images/integration-logos/constant-contact.png'
                    className={styles.logo}
                />
            ),
        });
    }

    menuOptions.push({
        label: <div className={styles.button}>Send to Quickbooks</div>,
        key: `${optionKey++}`,
        sortKey: 'Send to Quickbooks',
        handleSelect: onQuickbooksSelect,
        icon: (
            <img src='/include/images/integration-logos/quickbooks.png' className={styles.logo} />
        ),
    });

    // @ts-expect-error
    lead.sendToDestinations.forEach((destination) => {
        // @ts-expect-error
        menuOptions.push({
            label: <Link href={destination.localUrl}>{destination.name}</Link>,
            key: `${optionKey++}`,
            sortKey: destination.name,
            // The icon for custom send-tos is just an empty div. This spaces the
            // items corectly, lining them up with the other items, but we don't
            // have an actual logo to show.
            icon: <div className={styles.logo} />,
        });
    });

    if (user && user.permissions.canDeleteEntities) {
        if (lead.deletedTime === null) {
            menuOptions.push({
                label: <div className={styles.delete}>Delete lead</div>,
                key: `${optionKey++}`,
                // @ts-expect-error
                variant: 'destructive',
                sortKey: 'ZZ Delete lead', // Always last
                handleSelect: onDeleteSelect,
                icon: <TrashIcon size={20} />,
            });
        } else {
            menuOptions.push({
                label: <div className={styles.button}>Undelete</div>,
                key: `${optionKey++}`,
                // @ts-expect-error
                variant: 'secondary',
                sortKey: 'ZZ Undelete', // Always last
                handleSelect: () => {
                    Nut.ajax.post({
                        url: `/lead/${lead.id}/undelete.json`,
                    });
                },
                icon: <TrashEmptyIcon size={20} />,
            });

            // GREP: NOT using canDeleteEntities for permanently deleting
            if (user.permissions.canAccessSetup && lead.deletedTime === null) {
                menuOptions.push({
                    label: <div className={styles.delete}>Permanently Delete</div>,
                    key: `${optionKey++}`,
                    // @ts-expect-error
                    variant: 'destructive',
                    sortKey: 'ZZZ Permanently Delete', // Always last
                    handleSelect: onDeleteSelect,
                    icon: <TrashIcon size={20} />,
                });
            }
        }
    }

    menuOptions.sort((a, b) => {
        return a.sortKey.localeCompare(b.sortKey);
    });

    return menuOptions;
}
